/**
 * HCZM recurring billing compliance.
 *
 *   1. Reveal the affirmative consent checkbox only while a recurring
 *      option is selected, and block add-to-cart until it is ticked.
 *   2. Keep the displayed rebill price in sync with the selected variant.
 *
 * Fail-open by design: if this script does not load, one-time purchases are
 * never blocked. It only ever adds a gate to a recurring selection.
 *
 * v2 - removed three self-triggering MutationObservers. The previous version
 * observed a subtree it also wrote into, so every DOM write re-fired the
 * observer and the page locked up. Updates are now event-driven and batched
 * into a single pass, and every write is compared before it is applied.
 */
(function () {
  'use strict';

  var RECURRING_PATTERNS = [
    'subscribe', 'subscription', 'auto-ship', 'autoship', 'auto ship',
    'deliver every', 'every month', 'monthly delivery', 'recurring'
  ];

  // Matches "Blossom Body Oil - Sub", "Breathe+ -Sub" but not "substance".
  var SUB_SUFFIX = /[-\u2013\u2014]\s*sub\b/;

  function looksRecurring(text) {
    if (!text) return false;
    var t = String(text).toLowerCase();
    if (SUB_SUFFIX.test(t)) return true;
    for (var i = 0; i < RECURRING_PATTERNS.length; i++) {
      if (t.indexOf(RECURRING_PATTERNS[i]) !== -1) return true;
    }
    return false;
  }

  /** Write only when the value differs. No needless DOM churn. */
  function setText(el, v) { if (el && el.textContent !== v) el.textContent = v; }
  function setHidden(el, v) { if (el && el.hidden !== v) el.hidden = v; }

  function findScope(root) {
    return root.closest('product-information') ||
           root.closest('product-form-component') ||
           root.closest('section') || document;
  }

  function findForm(scope) {
    return scope.querySelector ? scope.querySelector('form[action*="/cart/add"]') : null;
  }

  function isRecurringSelected(scope) {
    var plan = scope.querySelector(
      'input[name="selling_plan"]:checked, select[name="selling_plan"], input[name="selling_plan"][type="hidden"]'
    );
    if (plan && plan.value && plan.value !== '' && plan.value !== '0') return true;

    var checked = scope.querySelectorAll(
      '.variant-option input[type="radio"]:checked, variant-picker input[type="radio"]:checked'
    );
    for (var i = 0; i < checked.length; i++) {
      var label = checked[i].closest('label');
      if (looksRecurring(checked[i].value)) return true;
      if (label && looksRecurring(label.textContent)) return true;
    }

    var selects = scope.querySelectorAll('variant-picker select, .variant-option select');
    for (var j = 0; j < selects.length; j++) {
      var opt = selects[j].options[selects[j].selectedIndex];
      if (looksRecurring(selects[j].value)) return true;
      if (opt && looksRecurring(opt.textContent)) return true;
    }
    return false;
  }

  function selectedVariantId(scope) {
    var input = scope.querySelector('form[action*="/cart/add"] input[name="id"]') ||
                scope.querySelector('input[name="id"]') ||
                scope.querySelector('select[name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  function addToCartButtons(scope, form) {
    var seen = [];
    function push(list) {
      for (var i = 0; i < list.length; i++) {
        if (seen.indexOf(list[i]) === -1) seen.push(list[i]);
      }
    }
    if (form) push(form.querySelectorAll('[type="submit"], .add-to-cart-button, add-to-cart-component button'));
    if (scope && scope.querySelectorAll) {
      push(scope.querySelectorAll('.add-to-cart-button, add-to-cart-component button'));
      push(scope.querySelectorAll('.sticky-add-to-cart button, [ref="stickyAddToCart"] button'));
    }
    return seen;
  }

  /* ---------------------------------------------------------------- consent */

  function bindConsent(root) {
    if (root.__hczmBound) return null;
    root.__hczmBound = true;

    var consent = root.querySelector('[data-hczm-consent]');
    var input = root.querySelector('[data-hczm-consent-input]');
    var termsBody = root.querySelector('[data-hczm-terms-body]');
    if (!consent || !input) return null;

    var scope = findScope(root);
    var form = findForm(scope);
    var version = root.getAttribute('data-hczm-terms-version') || '';

    function update() {
      var recurring = isRecurringSelected(scope);
      // Terms and consent both appear only once a subscription option is
      // selected. A one-time purchase never sees either.
      setHidden(termsBody, !recurring);
      setHidden(consent, !recurring);

      if (!recurring) {
        if (input.checked) { input.checked = false; input.value = ''; }
        if (consent.hasAttribute('data-hczm-consent-error')) {
          consent.removeAttribute('data-hczm-consent-error');
        }
      }

      var gate = recurring && !input.checked;
      var buttons = addToCartButtons(scope, form);
      for (var i = 0; i < buttons.length; i++) {
        var b = buttons[i];
        var gated = b.getAttribute('data-hczm-gated') === 'true';
        if (gate && !gated) {
          b.setAttribute('aria-disabled', 'true');
          b.setAttribute('data-hczm-gated', 'true');
        } else if (!gate && gated) {
          b.removeAttribute('aria-disabled');
          b.removeAttribute('data-hczm-gated');
        }
      }
    }

    input.addEventListener('change', function () {
      if (input.checked) {
        input.value = 'Agreed ' + new Date().toISOString() + ' (terms ' + version + ')';
        consent.removeAttribute('data-hczm-consent-error');
      } else {
        input.value = '';
      }
      update();
    });

    if (form && !form.__hczmSubmitBound) {
      form.__hczmSubmitBound = true;
      form.addEventListener('submit', function (e) {
        if (isRecurringSelected(scope) && !input.checked) {
          e.preventDefault();
          e.stopImmediatePropagation();
          consent.setAttribute('data-hczm-consent-error', 'true');
          if (consent.scrollIntoView) consent.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input.focus();
        }
      }, true);
    }

    if (!document.__hczmClickBound) {
      document.__hczmClickBound = true;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-hczm-gated="true"]');
        if (!btn) return;
        var owner = btn.closest('product-information') || btn.closest('section');
        var box = owner && owner.querySelector('[data-hczm-consent]');
        var chk = owner && owner.querySelector('[data-hczm-consent-input]');
        if (!box || !chk || chk.checked) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        box.setAttribute('data-hczm-consent-error', 'true');
        if (box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        chk.focus();
      }, true);
    }

    return update;
  }

  /* ----------------------------------------------------------------- rebill */

  function bindRebill(box) {
    if (box.__hczmRebillBound) return null;
    box.__hczmRebillBound = true;

    var scope = findScope(box);
    var mapEl = scope.querySelector ? scope.querySelector('[data-hczm-rebill-map]') : null;
    if (!mapEl) return null;

    var map;
    try {
      map = JSON.parse(mapEl.textContent || '{}');
    } catch (err) {
      return null; // malformed map: render nothing rather than something wrong
    }

    var firstEl = box.querySelector('[data-hczm-rebill-first]');
    var thenEl = box.querySelector('[data-hczm-rebill-then]');
    var perMonthEl = box.querySelector('[data-hczm-rebill-permonth]');
    var shipEl = box.querySelector('[data-hczm-rebill-ship]');

    return function update() {
      var id = selectedVariantId(scope);
      var data = id && map[id];

      if (!data) { setHidden(box, true); return; }

      setHidden(box, false);
      setText(firstEl, data.firstText + ' today');
      setText(thenEl, 'then ' + data.rebillText + ' every ' + data.every +
                      ', automatically, until you cancel');

      if (data.perMonth) {
        setText(perMonthEl, 'Works out to ' + data.perMonth + ' per month.');
        setHidden(perMonthEl, false);
      } else {
        setHidden(perMonthEl, true);
      }

      if (data.ships) {
        setText(shipEl, 'Ships every ' + data.ships + '.');
        setHidden(shipEl, false);
      } else {
        setHidden(shipEl, true);
      }
    };
  }

  /* ------------------------------------------------------------ scheduling */

  var updaters = [];
  var queued = false;

  function runAll() {
    queued = false;
    for (var i = 0; i < updaters.length; i++) {
      try { updaters[i](); } catch (err) { /* one bad updater must not stop the rest */ }
    }
  }

  // Batched so a burst of events collapses into one pass. Nothing here observes
  // what it writes, so this cannot re-enter itself.
  function schedule() {
    if (queued) return;
    queued = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(runAll);
    else window.setTimeout(runAll, 0);
  }

  function init() {
    var added = false;
    var terms = document.querySelectorAll('[data-hczm-subscription-terms]');
    for (var i = 0; i < terms.length; i++) {
      var fn = bindConsent(terms[i]);
      if (fn) { updaters.push(fn); added = true; }
    }
    var boxes = document.querySelectorAll('[data-hczm-rebill]');
    for (var j = 0; j < boxes.length; j++) {
      var rf = bindRebill(boxes[j]);
      if (rf) { updaters.push(rf); added = true; }
    }
    if (added) schedule();
  }

  // Delegated on document so a re-rendered form stays covered without rebinding.
  ['change', 'variant:update', 'variantChange', 'product:variant-change'].forEach(function (evt) {
    document.addEventListener(evt, schedule, true);
  });

  document.addEventListener('shopify:section:load', function () { init(); schedule(); });
  document.addEventListener('shopify:block:select', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
